import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { filterVisibleQuestions, omitHiddenAnswers } from '../../utils/conditionalDisplay';
import { resolveNextRoute, isControlledSubstanceProduct } from '../../utils/intakeRouting';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import * as productService from '../../services/productService';
import * as intakeService from '../../services/intakeService';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import OfferLoadingScreen from '../../components/common/OfferLoadingScreen';
import api from '../../services/api';
import styles from './IntakePage.module.css';


/** Parse height text to total inches. Handles "5'6\"", "66", "5 6", "5ft 6in" */
const parseHeightToInches = (heightStr) => {
  if (!heightStr) return null;
  const s = String(heightStr).trim();
  const feetInches = s.match(/^(\d+)\s*['\'\u2019ft]+\s*(\d+)\s*(?:in|"|\u201d|\s*$)/i)
    || s.match(/^(\d+)\s+(\d{1,2})$/);
  if (feetInches) {
    const total = parseInt(feetInches[1], 10) * 12 + parseInt(feetInches[2], 10);
    return total >= 30 && total <= 96 ? total : null;
  }
  const plain = parseInt(s, 10);
  if (!isNaN(plain) && plain >= 30 && plain <= 96) return plain;
  return null;
};

/** Parse weight text to pounds (integer). Handles "180", "180.5", "180 lbs" */
const parseWeightToLbs = (weightStr) => {
  if (!weightStr) return null;
  const n = Math.round(parseFloat(String(weightStr).replace(/[^\d.]/g, '')));
  return !isNaN(n) && n >= 50 && n <= 800 ? n : null;
};

/**
 * Pure. The `requiredValue` rule on a field's stored `validation` list, or null.
 *
 * A `required` rule only demands that a question be answered AT ALL, which an
 * attestation satisfies with its own NEGATION — "I confirm that all information
 * provided is true and accurate." accepted "No" and submitted (card 31578).
 * A `requiredValue` rule names the answer that must actually be given.
 */
const findAttestationRule = (validation) =>
  (Array.isArray(validation) ? validation : []).find((rule) => rule?.type === 'requiredValue') ?? null;

/** Pure. True when a question demands a specific answer and the given one is not it. */
const isAttestationUnmet = (question, answer) => {
  const demanded = question?.attestationRule?.value;
  if (demanded === null || demanded === undefined) return false;
  return String(answer ?? '').toLowerCase() !== String(demanded).toLowerCase();
};

/** The message a blocked attestation shows. Never a silent no-op. */
const formatAttestationError = (question) =>
  question?.attestationRule?.message ?? 'You must answer Yes to continue.';

// Textarea fields that get a quick-select "None" button (medical history free-text)
const NONE_BUTTON_TEXTAREA_FIELDS = [
  'allergies',
  'current_medications',
  'medical_conditions',
  'what_glp_medications_current_dose',
  'peptide_form_and_dosage',
  'reaction_type_and_peptides'
];

// ── TRT recent-lab upload (Project Testosterone, Phase C2) ──────────────────────
// The ONE file-type field the dynamic intake form is allowed to render itself.
// Every other file field stays filtered out: the hardcoded government-ID / selfie
// step at the end of this wizard owns those, and letting the dynamic renderer draw
// them too would put a second ID uploader on the page.
const TRT_LAB_UPLOAD_FIELD_ID = 'upload_your_most_recent_lab_results_dated_within_past_60_days';
// Optional prior/existing prescription (card 28374). APPLICABILITY IS DATA, NOT A PRODUCT:
// the control renders only when the product's resolved DoctorNow intake form actually
// declares a `file` field with this id, so no product slug/name/UUID appears here.
const PRIOR_PRESCRIPTION_FIELD_ID = 'prior_prescription';
// The persisted document's `type`, written by api/controllers/intakeController.js and read
// by card 28376's vendor forward. Deliberately its OWN const even though it currently holds
// the same literal as the field id above: they are two different contracts (a form field vs
// a stored document), and renaming one without the other must not silently pass.
const PRIOR_PRESCRIPTION_DOCUMENT_TYPE = 'prior_prescription';

// Its trigger. ⚠️ The option values on this field are lower-case `yes` / `no`, while
// `trt_previous_treatment` on the SAME form ships `Yes` / `No` — vendor option case is
// inconsistent by design and `trtIntakeFieldParity.test.js` pins both. Comparisons here
// go through the same case-insensitive normalisation `conditionalDisplay.js` uses, so
// neither literal has to be "corrected" (correcting either one silently breaks a gate).
const TRT_BLOODWORK_TRIGGER_FIELD_ID = 'have_you_had_bloodwork_done_and_completed_within_the_past_60_days';

// TESTOSTERONE-LAUNCH-PLAN.md §6 Q6 — DoctorNow has NOT confirmed the accepted file
// types or a size ceiling for lab documents. These are Oval's interim, reversible
// choices, set deliberately EQUAL to the existing government-ID upload so that one
// vendor answer retunes both, and equal to the server-side `documentFileFilter` in
// api/middleware/upload.js so the client never accepts a file the API will reject.
const LAB_UPLOAD_ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const LAB_UPLOAD_MAX_MB = 10;
const LAB_UPLOAD_MAX_BYTES = LAB_UPLOAD_MAX_MB * 1024 * 1024;

const formatMb = (bytes) => (bytes / (1024 * 1024)).toFixed(1);

/** Case-insensitive scalar read of an answer, matching conditionalDisplay's normalisation. */
const readScalarAnswer = (answers, fieldId) => String(answers?.[fieldId] ?? '').trim().toLowerCase();

const IntakePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { isDemo } = useAuth();

  const [product, setProduct] = useState(null);
  const [intakeForm, setIntakeForm] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOfferTransition, setShowOfferTransition] = useState(false);
  const [redirectTarget, setRedirectTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [licenseFront, setLicenseFront] = useState(null);
  const [licenseBack, setLicenseBack] = useState(null);
  const [existingDocuments, setExistingDocuments] = useState(null);
  const [useSavedId, setUseSavedId] = useState(false);
  const [statusBlocked, setStatusBlocked] = useState(null);
  // The lab document itself is a File — it cannot live in `answers`, which is
  // plain JSON component state. `answers` holds only the filename, which is what
  // makes the field satisfy required-validation.
  const [labResultsFile, setLabResultsFile] = useState(null);
  const [priorPrescriptionFile, setPriorPrescriptionFile] = useState(null);
  // Documents already persisted on this patient's submission for this product, so a
  // prescription they uploaded earlier reads back as attached instead of being asked
  // for twice (card 28374). Separate from `existingDocuments`, which is the reusable
  // government-ID pair and is keyed by `front`/`back` rather than by document type.
  const [existingSubmissionDocuments, setExistingSubmissionDocuments] = useState(null);
  const [labUploadError, setLabUploadError] = useState('');
  // The product's real clinical questions could not be READ. Deliberately distinct
  // from "this product has no intake form", which is a 404 and still renders the
  // defaults — conflating the two is the defect this pair fixes (card 53593).
  const [formLoadError, setFormLoadError] = useState(false);
  const [formReloadNonce, setFormReloadNonce] = useState(0);

  // Calculate steps based on loaded questions
  const requiresLicense = intakeForm?.settings?.requiresDriversLicense !== false;
  // Conditional fields are evaluated against live answers, so the wizard walks
  // the VISIBLE list — a hidden question must not consume a step or a progress slot.
  const visibleQuestions = useMemo(() => filterVisibleQuestions(questions, answers), [questions, answers]);
  const totalSteps = visibleQuestions.length + (requiresLicense ? 1 : 0);
  const isLicenseStep = requiresLicense && currentStep === visibleQuestions.length;
  const currentQuestion = visibleQuestions[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Answering a trigger question can hide later questions and shrink the list
  // under the cursor; clamp so currentStep never points past the license step.
  useEffect(() => {
    setCurrentStep((prev) => Math.min(prev, totalSteps - 1 < 0 ? 0 : totalSteps - 1));
  }, [totalSteps]);

  useEffect(() => {
    const loadData = async () => {
      // A retry starts from a clean slate and shows the spinner again: the previous
      // attempt's failure must not survive into an arm that succeeds, and the interim
      // must not flash the "No intake form available" empty state. `setLoading(true)`
      // is a no-op on first mount, where it already is.
      setFormLoadError(false);
      setLoading(true);
      try {
        // Load product
        const intakePreview = searchParams.get("preview");
        const productResponse = await productService.getProductBySlug(slug, { preview: intakePreview });
        const loadedProduct = productResponse.product;
        setProduct(loadedProduct);

        // Preview mode bypasses status checks
        const previewParam = searchParams.get('preview');
        const isPreview = previewParam === loadedProduct?.id;
        // Block intake for non-active products
        if (!isPreview && loadedProduct?.isComingSoon) {
          setStatusBlocked({
            title: 'Coming Soon',
            message: 'This product is coming soon and is not yet available.'
          });
          setLoading(false);
          return;
        }
        if (!isPreview && loadedProduct?.isOutOfStock) {
          setStatusBlocked({
            title: 'Out of Stock',
            message: 'This product is currently out of stock.'
          });
          setLoading(false);
          return;
        }

        // Load intake form for this product
        if (loadedProduct?.id) {
          try {
            const formResponse = await intakeService.getIntakeForm(loadedProduct.id, { preview: previewParam });
            if (formResponse && formResponse.fields && formResponse.fields.length > 0) {
              setIntakeForm(formResponse);
              // Transform fields to questions format
              const formQuestions = formResponse.fields
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .filter((field) => {
                  const fieldType = field.type ?? field.fieldType ?? field.field_type;
                  const fieldLabel = (field.label || '').toLowerCase();
                  // BMI is calculated at submit time, never asked.
                  if (fieldLabel.includes('bmi')) return false;
                  // File fields are filtered out by default — EXCEPT the TRT lab upload,
                  // which is the only one this form renders (see TRT_LAB_UPLOAD_FIELD_ID).
                  if (fieldType === 'file' || fieldType === 21) {
                    const id = field.fieldId ?? field.field_id;
                    return id === TRT_LAB_UPLOAD_FIELD_ID || id === PRIOR_PRESCRIPTION_FIELD_ID;
                  }
                  return true;
                })
                .map((field) => ({
                  id: field.fieldId,
                  section: field.section || 'General',
                  type: field.type,
                  question: field.label,
                  placeholder: field.placeholder,
                  helpText: field.helpText,
                  options: field.options || [],
                  optionValues: field.options,
                  required: field.validation?.some((v) => v.type === 'required') || false,
                  validation: field.validation,
                  attestationRule: findAttestationRule(field.validation),
                  allow_other: field.allowOther || field.allow_other || false,
                  conditionalDisplay: field.conditionalDisplay || field.conditional_display || []
                }));
              setQuestions(formQuestions);

              // Read back a prescription this patient already uploaded, so the control shows
              // it as attached instead of asking for it twice (card 28374).
              // GATED on the form actually declaring the field: an ungated read costs every
              // other intake a request that can only 404, and that 404 is a console error.
              // Its OWN try/catch on purpose — letting it reach the outer `catch (formError)`
              // would replace this form's real questions with getDefaultQuestions().
              const offersPriorPrescription = formResponse.fields.some(
                (field) => (field.fieldId ?? field.field_id) === PRIOR_PRESCRIPTION_FIELD_ID
              );
              if (offersPriorPrescription) {
                try {
                  const submission = await intakeService.getMySubmission(loadedProduct.id);
                  setExistingSubmissionDocuments(submission?.documents ?? null);
                } catch (submissionError) {
                  // No prior submission for this product yet — the ordinary first-time case.
                }
              }
            } else {
              // Form exists but has no fields, use defaults
              setQuestions(getDefaultQuestions());
            }
          } catch (formError) {
            // 404 is this endpoint's genuine "this product has no intake form" answer
            // (api/controllers/intakeController.js:102/:109) and MUST keep falling back
            // to the defaults. Every OTHER cause — a 403 from checkIntakeAvailability,
            // a 5xx, or a rejected fetch (which carries no `.status` at all, so it
            // fails CLOSED here) — means the product's REAL clinical questions could
            // not be read. Serving the generic set there lets a patient answer the
            // wrong questionnaire and SUBMIT it to a prescriber, with no BMI computed
            // and none of this form's attestations present (card 53593).
            if (formError?.status === 404) {
              setQuestions(getDefaultQuestions());
            } else {
              console.error('Intake form could not be loaded:', formError);
              setFormLoadError(true);
            }
          }
        } else {
          // No product ID, use default questions
          setQuestions(getDefaultQuestions());
        }

        // Load existing documents (for pre-filling)
        try {
          const docs = await intakeService.getMyDocuments();
          if (docs) {
            setExistingDocuments(docs);
          }
        } catch (docError) {
          // Ignore - user may not have existing documents
        }
      } catch (error) {
        toast.error('Product not found');
        navigate('/catalog');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [slug, navigate, toast, formReloadNonce]);

  // Intake drafts are deliberately NOT persisted in the browser. The restore and save
  // effects that used to sit here keyed their storage key off `product.categoryId`, a
  // field /api/products/<slug> does not return, so neither effect had ever run in
  // production. Restoring a *complete* draft also auto-navigated a returning patient
  // past the entire clinical form into checkout, on a draft scoped per product CATEGORY
  // rather than per product. Removed by board card 2238 on 2026-08-25 so that a future
  // addition of that field to the products payload cannot switch the behaviour on by
  // accident — which is exactly what happened on 2026-08-14.
  // ⛔ Do not re-add persistence as a null-safety tidy-up: it needs a product ruling on
  // the skip-the-whole-intake branch first.

  // Default questions if product has no custom form
  const getDefaultQuestions = () => [
    {
      id: 'height',
      section: 'Personal Information',
      type: 'height',
      question: 'What is your height?',
      required: true
    },
    {
      id: 'weight',
      section: 'Personal Information',
      type: 'weight',
      question: 'What is your current weight?',
      required: true
    },
    {
      id: 'dob',
      section: 'Personal Information',
      type: 'date',
      question: 'What is your date of birth?',
      required: true
    },
    {
      id: 'allergies',
      section: 'Medical History',
      type: 'textarea',
      question: 'Do you have any known drug allergies?',
      placeholder: 'List any allergies or type "None"',
      required: true
    },
    {
      id: 'conditions',
      section: 'Medical History',
      type: 'multi_select',
      question: 'Do you have any of the following conditions?',
      options: ['Heart disease', 'High blood pressure', 'Diabetes', 'Liver disease', 'Kidney disease', 'None of the above'],
      required: true
    },
    {
      id: 'medications',
      section: 'Current Medications',
      type: 'textarea',
      question: 'List all current medications and supplements',
      placeholder: 'Include prescription, over-the-counter, and supplements.',
      required: false
    }
  ];

  const handleAnswer = (value) => {
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: value
    }));
  };

  const isAnswerEmpty = (answer) => {
    if (answer === null || answer === undefined || answer === '') return true;
    if (Array.isArray(answer)) return answer.length === 0;
    if (typeof answer === 'object') {
      // Height: { feet, inches } — check if both empty
      if ('feet' in answer && 'inches' in answer) return !answer.feet && !answer.inches;
      // Select with allow_other: { selected, other }
      if ('selected' in answer) return !answer.selected && !answer.other;
      return Object.values(answer).every(v => !v);
    }
    return false;
  };

  const isLastQuestionStep = !requiresLicense && currentStep === visibleQuestions.length - 1;

  const handleNext = () => {
    if (currentQuestion?.required && isAnswerEmpty(answers[currentQuestion.id])) {
      toast.error('Please answer this question to continue');
      return;
    }
    // An attestation must not be satisfiable by its own negation (card 31578).
    if (isAttestationUnmet(currentQuestion, answers[currentQuestion?.id])) {
      toast.error(formatAttestationError(currentQuestion));
      return;
    }
    if (isLastQuestionStep) {
      handleSubmit();
      return;
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrev = () => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  };

  // Pure. The client-side upload rules, in ONE place now that more than one file question
  // can render. Reject on TYPE first, then SIZE; '' means accepted. The REAL guard is the
  // server's shared `DOCUMENT_MIME_TYPES` + size ceiling — this only spares a round trip.
  // Messages are byte-identical to the ones this replaced, so the lab flow is unchanged.
  const deriveUploadError = (file) => {
    if (!LAB_UPLOAD_ACCEPTED_MIME_TYPES.includes(file.type)) {
      return 'That file type is not supported. Please upload a PDF, JPG or PNG.';
    }
    if (file.size > LAB_UPLOAD_MAX_BYTES) {
      return `That file is ${formatMb(file.size)} MB. Please upload a file under ${LAB_UPLOAD_MAX_MB} MB.`;
    }
    return '';
  };

  // Clear the recorded answer on rejection either way, so a rejected file can never leave
  // the question looking answered.
  const handleLabResultsChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const uploadError = deriveUploadError(file);
    if (uploadError) {
      setLabUploadError(uploadError);
      setLabResultsFile(null);
      handleAnswer('');
      return;
    }
    setLabUploadError('');
    setLabResultsFile(file);
    handleAnswer(file.name);
  };

  const handlePriorPrescriptionChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const uploadError = deriveUploadError(file);
    if (uploadError) {
      setLabUploadError(uploadError);
      setPriorPrescriptionFile(null);
      handleAnswer('');
      return;
    }
    setLabUploadError('');
    setPriorPrescriptionFile(file);
    handleAnswer(file.name);
  };

  const handleLicenseChange = (type) => (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      if (type === 'front') {
        setLicenseFront(file);
      } else {
        setLicenseBack(file);
      }
    }
  };

  const handleSubmit = async () => {
    // Every VISIBLE attestation must carry its affirmative answer before anything is
    // persisted. handleNext already guards the step an attestation sits on; this also
    // covers the license step's own Submit button, which never routes through
    // handleNext. Hidden questions are excluded — they are not asked (card 31578).
    const unmetAttestation = visibleQuestions.find((q) => isAttestationUnmet(q, answers[q.id]));
    if (unmetAttestation) {
      toast.error(formatAttestationError(unmetAttestation));
      setCurrentStep(visibleQuestions.indexOf(unmetAttestation));
      return;
    }

    // Check if we have both uploads or existing documents (with useSavedId toggled)
    const hasFront = licenseFront || (useSavedId && existingDocuments?.front);
    const hasBack = licenseBack || (useSavedId && existingDocuments?.back);

    if (!hasFront || !hasBack) {
      toast.error('Please upload both front and back of your ID');
      return;
    }

    setSubmitting(true);
    try {
      // Auto-calculate BMI from height/weight answers
      // A patient who answered "Yes" then switched to "No" must not ship the
      // now-hidden follow-up answer to the vendor.
      const submissionAnswers = omitHiddenAnswers(questions, answers);
      const allFields = intakeForm?.fields || [];
      const bmiFields = allFields.filter(f => (f.label || '').toLowerCase().includes('bmi'));
      if (bmiFields.length > 0) {
        let heightInches = null;
        let weightLbs = null;
        for (const field of allFields) {
          const label = (field.label || '').toLowerCase();
          const answer = answers[field.fieldId];
          if (label.includes('height') && answer) {
            if (typeof answer === 'object' && 'feet' in answer) {
              const feet = parseInt(answer.feet, 10) || 0;
              const inches = parseInt(answer.inches, 10) || 0;
              const total = feet * 12 + inches;
              heightInches = total >= 30 && total <= 96 ? total : null;
            } else {
              heightInches = parseHeightToInches(String(answer));
            }
          }
          if (label.includes('weight') && answer) {
            weightLbs = parseWeightToLbs(String(typeof answer === 'object' ? JSON.stringify(answer) : answer));
          }
        }
        for (const bmiField of bmiFields) {
          if (heightInches && weightLbs && heightInches > 0) {
            submissionAnswers[bmiField.fieldId] = ((weightLbs / (heightInches * heightInches)) * 703).toFixed(1);
          } else {
            submissionAnswers[bmiField.fieldId] = 'N/A';
          }
        }
      }

      // Send useExistingDocuments flag if user opted to use saved ID and didn't upload new files
      const useExistingDocuments = useSavedId && !licenseFront && !licenseBack;
      const submitPreview = searchParams.get('preview');
      // A patient who answered "Yes", attached labs, then switched to "No" must not
      // ship the now-hidden document. `omitHiddenAnswers` already drops the ANSWER;
      // this drops the FILE, keyed off the same visibility rule rather than a second
      // hand-rolled condition that could drift away from it.
      const labQuestionVisible = visibleQuestions.some((q) => q.id === TRT_LAB_UPLOAD_FIELD_ID);
      // Same visibility rule, same reason (card 28374): never ship a file for a question the
      // patient can no longer see. Keyed off `visibleQuestions` rather than a second
      // hand-rolled condition that could drift away from it.
      const priorPrescriptionVisible = visibleQuestions.some((q) => q.id === PRIOR_PRESCRIPTION_FIELD_ID);

      await intakeService.submitIntakeForm(product.id, {
        preview: submitPreview,
        answers: submissionAnswers,
        licenseFront,
        licenseBack,
        labResults: labQuestionVisible ? labResultsFile : null,
        priorPrescription: priorPrescriptionVisible ? priorPrescriptionFile : null,
        useExistingDocuments
      });
      toast.success('Intake submitted successfully!');

      // A CONTROLLED product must never be diverted here (card 7237). This branch
      // `return`s to /offers/... BEFORE resolveNextRoute is ever reached, so an active
      // promotion would walk a controlled substance straight past the lab-upload step.
      // The clinical gate outranks the promotion.
      //
      // DECISION 2026-09-03 (reversible). ROLLBACK: delete this `if` and its closing
      // brace and re-indent the block, restoring offer-first behaviour exactly.
      if (!isControlledSubstanceProduct(product)) {
        // After intake completion, resolve trigger to determine redirect
        // Uses unified endpoint that checks: 1) product-specific super offer, 2) category super offer
        try {
          const catId = product.categoryId || product.category?.id;
          const triggerRes = await api.get('/offers/resolve-trigger', {
            params: { productId: product.id, categoryId: catId || undefined }
          });

          if (triggerRes.offer && triggerRes.offer.slug) {
            const offerSlug = triggerRes.offer.slug;
            const triggerType = triggerRes.triggerType;
            // For product-specific triggers, pass the original product so the offer page can show skip
            const originalParam = triggerType === 'product' ? `&originalProduct=${encodeURIComponent(product.slug)}` : '';
            const savedDosageIdForOffer = searchParams.get('dosage') || sessionStorage.getItem('selectedDosageId') || '';
            const dosageParam = savedDosageIdForOffer ? `&dosage=${savedDosageIdForOffer}` : '';
            setRedirectTarget(`/offers/${offerSlug}?from=intake${originalParam}${dosageParam}`);
            setShowOfferTransition(true);
            return;
          }
        } catch (err) {
          console.warn('Could not resolve offer trigger:', err);
        }
      }

      // No active offers - direct to calendar (DrCare) or checkout (Doctor Now)
      const nextRoute = resolveNextRoute(product);
      const savedDosageId = searchParams.get('dosage') || sessionStorage.getItem('selectedDosageId') || '';
      navigate(`${nextRoute}?product=${product.slug}${sessionStorage.getItem('selectedSupplyMonths') ? '&supplyMonths=' + sessionStorage.getItem('selectedSupplyMonths') : ''}${savedDosageId ? '&dosage=' + savedDosageId : ''}`);
    } catch (error) {
      toast.error(error.message || 'Failed to submit intake');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestionInput = () => {
    if (!currentQuestion) return null;
    const value = answers[currentQuestion.id] ?? '';

    // Auto-detect height/weight fields from text type
    let fieldType = currentQuestion.type;
    if (fieldType === "text") {
      const fid = (currentQuestion.id || "").toLowerCase();
      const flabel = (currentQuestion.question || currentQuestion.label || "").toLowerCase();
      if (fid.includes("height") || flabel.includes("height")) fieldType = "height";
      if (fid.includes("weight") || flabel.includes("weight")) fieldType = "weight";
    }

    switch (fieldType) {
      case 'height':
        return (
          <div className={styles.heightInput}>
            <div className={styles.heightField}>
              <input
                type="number"
                placeholder="5"
                min="3"
                max="8"
                value={value?.feet || ''}
                onChange={(e) => handleAnswer({ ...value, feet: e.target.value })}
                className={styles.input}
              />
              <span className={styles.unit}>ft</span>
            </div>
            <div className={styles.heightField}>
              <input
                type="number"
                placeholder="10"
                min="0"
                max="11"
                value={value?.inches || ''}
                onChange={(e) => handleAnswer({ ...value, inches: e.target.value })}
                className={styles.input}
              />
              <span className={styles.unit}>in</span>
            </div>
          </div>
        );

      case 'weight':
        return (
          <div className={styles.weightInput}>
            <input
              type="number"
              placeholder="150"
              min="50"
              max="500"
              value={value}
              onChange={(e) => handleAnswer(e.target.value)}
              className={styles.input}
            />
            <span className={styles.unit}>lbs</span>
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleAnswer(e.target.value)}
            className={styles.input}
            max={new Date().toISOString().split('T')[0]}
          />
        );

      case 'select':
        return (
          <div className={styles.selectOptions}>
            {(currentQuestion.options || []).map((option) => {
              const optLabel = typeof option === 'object' ? option.label : option;
              const optValue = typeof option === 'object' ? option.value : option;
              const isSelected = typeof value === 'object' ? value?.selected === optValue : value === optValue;
              return (
                <button
                  key={optValue}
                  type="button"
                  className={`${styles.optionBtn} ${isSelected ? styles.selected : ''}`}
                  onClick={() => handleAnswer(currentQuestion.allow_other ? { selected: optValue, other: '' } : optValue)}
                >
                  {optLabel}
                </button>
              );
            })}
            {currentQuestion.allow_other && (
              <>
                <button
                  type="button"
                  className={`${styles.optionBtn} ${(typeof value === 'object' ? value?.selected : value) === '_other' ? styles.selected : ''}`}
                  onClick={() => handleAnswer({ selected: '_other', other: typeof value === 'object' ? value?.other || '' : '' })}
                >
                  Other (please specify)
                </button>
                {(typeof value === 'object' ? value?.selected : value) === '_other' && (
                  <input
                    type="text"
                    value={typeof value === 'object' ? value?.other || '' : ''}
                    onChange={(e) => handleAnswer({ selected: '_other', other: e.target.value })}
                    placeholder="Please specify..."
                    className={styles.input}
                    style={{ marginTop: '8px' }}
                    autoFocus
                  />
                )}
              </>
            )}
          </div>
        );

      case 'boolean':
        return (
          <div className={styles.booleanOptions}>
            <button
              type="button"
              className={`${styles.optionBtn} ${value === 'yes' ? styles.selected : ''}`}
              onClick={() => handleAnswer('yes')}
            >
              Yes
            </button>
            <button
              type="button"
              className={`${styles.optionBtn} ${value === 'no' ? styles.selected : ''}`}
              onClick={() => handleAnswer('no')}
            >
              No
            </button>
            <button
              type="button"
              className={`${styles.optionBtn} ${value === 'na' ? styles.selected : ''}`}
              onClick={() => handleAnswer('na')}
            >
              Not applicable
            </button>
          </div>
        );

      case 'multiselect':
      case 'multi_select':
        {
          const msVal = currentQuestion.allow_other && typeof value === 'object' && !Array.isArray(value)
            ? value : { selections: Array.isArray(value) ? value : [], other: '' };
          const msSelections = currentQuestion.allow_other ? (msVal.selections || []) : (Array.isArray(value) ? value : []);
          const msOtherText = currentQuestion.allow_other ? (msVal.other || '') : '';
          const msOtherSelected = currentQuestion.allow_other && msSelections.includes('_other');

          return (
            <div className={styles.multiselectOptions}>
              {(currentQuestion.options || []).map((option) => {
                const optLabel = typeof option === 'object' ? option.label : option;
                const optValue = typeof option === 'object' ? option.value : option;
                const selected = msSelections.includes(optValue);
                return (
                  <button
                    key={optValue}
                    type="button"
                    className={`${styles.optionBtn} ${selected ? styles.selected : ''}`}
                    onClick={() => {
                      const isNone = optValue.toLowerCase().includes('none');
                      let updated;
                      if (isNone) {
                        // None is mutually exclusive — clear all others
                        updated = selected ? [] : [optValue];
                      } else {
                        // Remove any "none" option first, then toggle
                        const withoutNone = msSelections.filter((v) => !v.toLowerCase().includes('none'));
                        updated = selected
                          ? withoutNone.filter((v) => v !== optValue)
                          : [...withoutNone, optValue];
                      }
                      handleAnswer(currentQuestion.allow_other ? { selections: updated, other: msOtherText } : updated);
                    }}
                  >
                    {selected && <span className={styles.checkmark}>✓</span>}
                    {optLabel}
                  </button>
                );
              })}
              {currentQuestion.allow_other && (
                <>
                  <button
                    type="button"
                    className={`${styles.optionBtn} ${msOtherSelected ? styles.selected : ''}`}
                    onClick={() => {
                      const updated = msOtherSelected
                        ? msSelections.filter((v) => v !== '_other')
                        : [...msSelections, '_other'];
                      handleAnswer({ selections: updated, other: msOtherText });
                    }}
                  >
                    {msOtherSelected && <span className={styles.checkmark}>✓</span>}
                    Other (please specify)
                  </button>
                  {msOtherSelected && (
                    <input
                      type="text"
                      value={msOtherText}
                      onChange={(e) => handleAnswer({ selections: msSelections, other: e.target.value })}
                      placeholder="Please specify..."
                      className={styles.input}
                      style={{ marginTop: '8px' }}
                      autoFocus
                    />
                  )}
                </>
              )}
            </div>
          );
        }

      case 'radio':
        return (
          <div className={styles.selectOptions}>
            {(currentQuestion.options || []).map((option) => {
              const optLabel = typeof option === 'object' ? option.label : option;
              const optValue = typeof option === 'object' ? option.value : option;
              const isSelected = typeof value === 'object' ? value?.selected === optValue : value === optValue;
              return (
                <button
                  key={optValue}
                  type="button"
                  className={`${styles.optionBtn} ${isSelected ? styles.selected : ''}`}
                  onClick={() => handleAnswer(currentQuestion.allow_other ? { selected: optValue, other: '' } : optValue)}
                >
                  {optLabel}
                </button>
              );
            })}
            {currentQuestion.allow_other && (
              <>
                <button
                  type="button"
                  className={`${styles.optionBtn} ${(typeof value === 'object' ? value?.selected : value) === '_other' ? styles.selected : ''}`}
                  onClick={() => handleAnswer({ selected: '_other', other: typeof value === 'object' ? value?.other || '' : '' })}
                >
                  Other (please specify)
                </button>
                {(typeof value === 'object' ? value?.selected : value) === '_other' && (
                  <input
                    type="text"
                    value={typeof value === 'object' ? value?.other || '' : ''}
                    onChange={(e) => handleAnswer({ selected: '_other', other: e.target.value })}
                    placeholder="Please specify..."
                    className={styles.input}
                    style={{ marginTop: '8px' }}
                    autoFocus
                  />
                )}
              </>
            )}
          </div>
        );

      case 'checkbox':
        {
          const cbVal = currentQuestion.allow_other && typeof value === 'object' && !Array.isArray(value)
            ? value : { selections: Array.isArray(value) ? value : [], other: '' };
          const cbSelections = currentQuestion.allow_other ? (cbVal.selections || []) : (Array.isArray(value) ? value : []);
          const cbOtherText = currentQuestion.allow_other ? (cbVal.other || '') : '';
          const cbOtherSelected = currentQuestion.allow_other && cbSelections.includes('_other');

          return (
            <div className={styles.multiselectOptions}>
              {(currentQuestion.options || []).map((option) => {
                const optLabel = typeof option === 'object' ? option.label : option;
                const optValue = typeof option === 'object' ? option.value : option;
                const selected = cbSelections.includes(optValue);
                return (
                  <button
                    key={optValue}
                    type="button"
                    className={`${styles.optionBtn} ${selected ? styles.selected : ''}`}
                    onClick={() => {
                      const isNone = optValue.toLowerCase().includes('none');
                      let updated;
                      if (isNone) {
                        // None is mutually exclusive — clear all others
                        updated = selected ? [] : [optValue];
                      } else {
                        // Remove any "none" option first, then toggle
                        const withoutNone = cbSelections.filter((v) => !v.toLowerCase().includes('none'));
                        updated = selected
                          ? withoutNone.filter((v) => v !== optValue)
                          : [...withoutNone, optValue];
                      }
                      handleAnswer(currentQuestion.allow_other ? { selections: updated, other: cbOtherText } : updated);
                    }}
                  >
                    {selected && <span className={styles.checkmark}>{"\u2713"}</span>}
                    {optLabel}
                  </button>
                );
              })}
              {currentQuestion.allow_other && (
                <>
                  <button
                    type="button"
                    className={`${styles.optionBtn} ${cbOtherSelected ? styles.selected : ''}`}
                    onClick={() => {
                      const updated = cbOtherSelected
                        ? cbSelections.filter((v) => v !== '_other')
                        : [...cbSelections, '_other'];
                      handleAnswer({ selections: updated, other: cbOtherText });
                    }}
                  >
                    {cbOtherSelected && <span className={styles.checkmark}>{"\u2713"}</span>}
                    Other (please specify)
                  </button>
                  {cbOtherSelected && (
                    <input
                      type="text"
                      value={cbOtherText}
                      onChange={(e) => handleAnswer({ selections: cbSelections, other: e.target.value })}
                      placeholder="Please specify..."
                      className={styles.input}
                      style={{ marginTop: '8px' }}
                      autoFocus
                    />
                  )}
                </>
              )}
            </div>
          );
        }

      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleAnswer(e.target.value)}
            placeholder={currentQuestion.placeholder}
            className={styles.input}
          />
        );

      case 'file': {
        const isLabUpload = currentQuestion.id === TRT_LAB_UPLOAD_FIELD_ID;
        const isPriorPrescription = currentQuestion.id === PRIOR_PRESCRIPTION_FIELD_ID;
        // One file question renders per step, so the two share `labUploadError`.
        const selectedFile = isPriorPrescription ? priorPrescriptionFile : labResultsFile;
        // An earlier upload, read back off the submission by document TYPE — never by
        // product. Scoped to the prescription on purpose: the TRT lab step keeps exactly
        // today's behaviour (this card's notes require its copy not to drift).
        const persistedDocument = isPriorPrescription
          ? (existingSubmissionDocuments ?? []).find((doc) => doc?.type === PRIOR_PRESCRIPTION_DOCUMENT_TYPE)
          : null;
        const attachedName = selectedFile?.name ?? persistedDocument?.filename;
        const onFileChange = isPriorPrescription ? handlePriorPrescriptionChange : handleLabResultsChange;
        const uploadText = isPriorPrescription ? 'Upload your prescription' : 'Upload your lab results';
        const inputId = `intake-file-${currentQuestion.id}`;
        return (
          <div className={styles.labUpload}>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={onFileChange}
              className={styles.fileInput}
              id={inputId}
            />
            <label htmlFor={inputId} className={styles.uploadLabel}>
              {attachedName ? (
                <div className={styles.uploadedFile}>
                  <span className={styles.fileIcon}>✓</span>
                  <span className={styles.fileName}>{attachedName}</span>
                  <span className={styles.changeFile}>Click to change</span>
                </div>
              ) : (
                <>
                  <span className={styles.uploadIcon}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                  </span>
                  <span className={styles.uploadText}>{uploadText}</span>
                  <span className={styles.uploadHint}>PDF, JPG or PNG · up to {LAB_UPLOAD_MAX_MB} MB</span>
                </>
              )}
            </label>
            {labUploadError && (
              <p className={styles.uploadError} role="alert">{labUploadError}</p>
            )}
            {(isLabUpload || isPriorPrescription) && currentQuestion.helpText && (
              <p className={styles.uploadNote}>{currentQuestion.helpText}</p>
            )}
          </div>
        );
      }

      case 'textarea': {
        const showNoneButton = NONE_BUTTON_TEXTAREA_FIELDS.includes(currentQuestion.id);
        const noneSelected = typeof value === 'string' && value.trim().toLowerCase() === 'none';
        return (
          <div>
            <textarea
              value={value}
              onChange={(e) => handleAnswer(e.target.value)}
              placeholder={currentQuestion.placeholder}
              className={styles.textarea}
              rows={4}
            />
            {showNoneButton && (
              <button
                type="button"
                className={`${styles.optionBtn} ${noneSelected ? styles.selected : ''}`}
                style={{ marginTop: '8px' }}
                onClick={() => handleAnswer(noneSelected ? '' : 'None')}
              >
                {noneSelected && <span className={styles.checkmark}>✓</span>}
                None
              </button>
            )}
          </div>
        );
      }

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleAnswer(e.target.value)}
            className={styles.input}
          />
        );
    }
  };

  // Show offer loading screen during redirect transition
  if (showOfferTransition) {
    const handleTransitionComplete = () => {
      if (redirectTarget) {
        navigate(redirectTarget);
      }
    };
    return <OfferLoadingScreen onComplete={handleTransitionComplete} />;
  }

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (statusBlocked) {
    return (
      <div className={styles.page}>
        <div className={styles.statusBlockedOverlay}>
          <div className={styles.statusBlockedPopup}>
            <div className={styles.statusBlockedIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h2 className={styles.statusBlockedTitle}>{statusBlocked.title}</h2>
            <p className={styles.statusBlockedMessage}>{statusBlocked.message}</p>
            <button
              type="button"
              className={styles.statusBlockedBtn}
              onClick={() => navigate('/catalog')}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Withholding the questionnaire withholds the SUBMIT with it. A generic answer set
  // persisted against a real prescriber is the harm this guards — so this gate sits
  // ABOVE the empty-state render, which is the legitimate 404 arm (card 53593).
  if (formLoadError) {
    return (
      <div className={styles.page}>
        <div className={styles.card} role="alert">
          <h1 className={styles.question}>We couldn&apos;t load this intake form</h1>
          <p className={styles.description}>
            Your medical questions could not be loaded, so this intake can&apos;t be
            started yet. Please try again in a moment.
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.backBtn} onClick={() => navigate('/catalog')}>
              Back to Catalog
            </button>
            <button
              type="button"
              className={styles.nextBtn}
              onClick={() => setFormReloadNonce((nonce) => nonce + 1)}
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (questions.length === 0 && !loading) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.question}>No intake form available</h1>
          <p className={styles.description}>
            This product does not have an intake form configured yet.
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.nextBtn} onClick={() => navigate('/catalog')}>
              Back to Catalog
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {currentStep > 0 && (
        <button type="button" className={styles.backArrow} onClick={handlePrev} aria-label="Go back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
        </button>
      )}
      {/* Progress Bar */}
      <div className={styles.progressContainer}>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progress}%` }} />
        </div>
        <span className={styles.progressText}>
          {currentStep + 1} of {totalSteps}
        </span>
      </div>

      {/* Product Info */}

      <div className={styles.productInfo}>
        <span className={styles.productLabel}>Requesting</span>
        <h2 className={styles.productName}>{product?.category?.name || product?.name}</h2>
      </div>

      {/* Question Card */}
      <div className={styles.card}>
        {!isLicenseStep ? (
          <>
            <div className={styles.sectionLabel}>{currentQuestion.section}</div>
            <h1 className={styles.question}>{currentQuestion.question}</h1>

            <div className={styles.inputContainer}>
              {renderQuestionInput()}
              {currentQuestion.id === TRT_BLOODWORK_TRIGGER_FIELD_ID
                && readScalarAnswer(answers, TRT_BLOODWORK_TRIGGER_FIELD_ID) === 'no' && (
                <div className={styles.blockedNotice} role="status">
                  <span className={styles.blockedNoticeTitle}>We&rsquo;ll follow up about lab work</span>
                  <p className={styles.blockedNoticeBody}>
                    Recent lab results are part of the clinical review for testosterone therapy.
                    The process for patients without bloodwork from the past 60 days is still being
                    confirmed with our partner clinical network, so we can&rsquo;t complete that step
                    here yet. Your answers will be reviewed by the care team, who will contact you
                    about lab work before any prescription decision is made.
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className={styles.sectionLabel}>Identity Verification</div>
            <h1 className={styles.question}>Upload your driver's license or government ID</h1>
            <p className={styles.description}>
              Please upload both the front and back of your ID. This is required for prescription verification.
            </p>

            {/* Show "Use Saved ID" toggle if user has existing documents */}
            {existingDocuments?.front && existingDocuments?.back && (
              <div className={styles.savedIdToggle}>
                <label className={styles.savedIdLabel}>
                  <input
                    type="checkbox"
                    checked={useSavedId}
                    onChange={(e) => setUseSavedId(e.target.checked)}
                    className={styles.savedIdCheckbox}
                  />
                  <span className={styles.savedIdText}>Use ID on file</span>
                </label>
              </div>
            )}

            {/* Show saved ID previews if toggled on */}
            {useSavedId && existingDocuments?.front && existingDocuments?.back ? (
              <div className={styles.savedIdPreview}>
                <div className={styles.savedIdCard}>
                  <div className={styles.savedIdTitle}>Front of ID</div>
                  <img
                    src={existingDocuments.front.path}
                    alt="ID Front"
                    className={styles.savedIdImage}
                  />
                </div>
                <div className={styles.savedIdCard}>
                  <div className={styles.savedIdTitle}>Back of ID</div>
                  <img
                    src={existingDocuments.back.path}
                    alt="ID Back"
                    className={styles.savedIdImage}
                  />
                </div>
              </div>
            ) : (
              <div className={styles.uploadGrid}>
                {/* Front of ID */}
                <div className={styles.uploadArea}>
                  <div className={styles.uploadTitle}>Front of ID</div>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleLicenseChange('front')}
                    className={styles.fileInput}
                    id="license-front"
                  />
                  <label htmlFor="license-front" className={styles.uploadLabel}>
                    {licenseFront ? (
                      <div className={styles.uploadedFile}>
                        <span className={styles.fileIcon}>✓</span>
                        <span className={styles.fileName}>{licenseFront.name}</span>
                        <span className={styles.changeFile}>Click to change</span>
                      </div>
                    ) : existingDocuments?.front ? (
                      <div className={styles.uploadedFile}>
                        <span className={styles.fileIcon}>✓</span>
                        <span className={styles.fileName}>Previously uploaded</span>
                        <span className={styles.changeFile}>Click to replace</span>
                      </div>
                    ) : (
                      <>
                        <span className={styles.uploadIcon}>
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                          </svg>
                        </span>
                        <span className={styles.uploadText}>Upload front</span>
                        <span className={styles.uploadHint}>PNG, JPG or PDF</span>
                      </>
                    )}
                  </label>
                </div>

                {/* Back of ID */}
                <div className={styles.uploadArea}>
                  <div className={styles.uploadTitle}>Back of ID</div>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleLicenseChange('back')}
                    className={styles.fileInput}
                    id="license-back"
                  />
                  <label htmlFor="license-back" className={styles.uploadLabel}>
                    {licenseBack ? (
                      <div className={styles.uploadedFile}>
                        <span className={styles.fileIcon}>✓</span>
                        <span className={styles.fileName}>{licenseBack.name}</span>
                        <span className={styles.changeFile}>Click to change</span>
                      </div>
                    ) : existingDocuments?.back ? (
                      <div className={styles.uploadedFile}>
                        <span className={styles.fileIcon}>✓</span>
                        <span className={styles.fileName}>Previously uploaded</span>
                        <span className={styles.changeFile}>Click to replace</span>
                      </div>
                    ) : (
                      <>
                        <span className={styles.uploadIcon}>
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                          </svg>
                        </span>
                        <span className={styles.uploadText}>Upload back</span>
                        <span className={styles.uploadHint}>PNG, JPG or PDF</span>
                      </>
                    )}
                  </label>
                </div>
              </div>
            )}
          </>
        )}



        {/* Navigation */}
        <div className={styles.actions}>
          {!isLicenseStep ? (
            <button type="button" className={styles.nextBtn} onClick={handleNext} disabled={isLastQuestionStep && submitting}>
              {isLastQuestionStep ? (submitting ? "Submitting..." : "Submit & Continue") : "Continue"}
            </button>
          ) : (
            <button
              type="button"
              className={styles.nextBtn}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit & Continue"}
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default IntakePage;
